import { PayloadAction } from '@reduxjs/toolkit';
import { monitoring } from 'common/services';
import { MediaTrackErrorKey } from 'devices/types';
import { addAppErrorMessage } from 'modules/alerts/state';
import { Effect } from 'store';
import {
  selectActiveAudioInputDevice,
  selectActiveVideoDevice,
  setAudioInputDeviceId,
  setVideoDeviceId,
} from 'user/state';

import { type TrackCreationMeta, agoraActions } from './agora.actions';
import { selectCurrentCameraDeviceId, selectCurrentMicrophoneDeviceId } from './agora.selectors';

// Audio related effects

type CreationCompleted = PayloadAction<undefined, string, TrackCreationMeta>;
type CreationFailed = PayloadAction<MediaTrackErrorKey, string, TrackCreationMeta>;

export const audioTrackCreationCompletedEffect: Effect<CreationCompleted> = ({ meta }, { dispatch, getState }) => {
  const activeAudioInputDevice = selectActiveAudioInputDevice(getState());

  if (activeAudioInputDevice?.deviceId) {
    dispatch(agoraActions.audioTrack.deviceChange.completed(activeAudioInputDevice?.deviceId));

    // Track microphone track creation
    monitoring.trackAction('video_service.microphone_track_created', {
      deviceId: activeAudioInputDevice.deviceId,
      durationMs: meta.durationMs,
    });
  }
};

export const audioTrackCreationFailedEffect: Effect<CreationFailed> = ({ payload: errorKey, meta }) => {
  // Specific tracking for backwards compatibility
  if (errorKey === 'deviceAlreadyInUse') {
    monitoring.trackAction('video_service.microphone_already_in_use');
  }

  // General tracking for aggregated failure metrics
  monitoring.trackAction('video_service.microphone_track_creation_failed', { errorKey, durationMs: meta.durationMs });
};

export const microphoneDeviceChangeCompletedEffect: Effect<PayloadAction<string>> = ({ payload: deviceId }) => {
  monitoring.trackAction('video_service.microphone_device_changed', { deviceId });
};

export const audioTrackTogglingFailedEffect: Effect = (_, { dispatch }) => {
  dispatch(addAppErrorMessage('Sorry we were unable to toggle your microphone at this time. Please try again.'));
};

export const microphoneDeviceChangeFailedEffect: Effect = (_, { dispatch, getState }) => {
  const currentMicrophoneId = selectCurrentMicrophoneDeviceId(getState());

  if (currentMicrophoneId) {
    dispatch(setAudioInputDeviceId(currentMicrophoneId));
  }

  monitoring.trackAction('video_service.microphone_device_change_failed');
  dispatch(addAppErrorMessage('Sorry we were unable to change your microphone at this time. Please try again.'));
};

// Video related effects

export const videoTrackCreationCompletedEffect: Effect<CreationCompleted> = ({ meta }, { dispatch, getState }) => {
  const activeVideoDevice = selectActiveVideoDevice(getState());

  if (activeVideoDevice?.deviceId) {
    dispatch(agoraActions.videoTrack.deviceChange.completed(activeVideoDevice?.deviceId));

    // Track camera track creation
    monitoring.trackAction('video_service.camera_track_created', {
      deviceId: activeVideoDevice.deviceId,
      durationMs: meta.durationMs,
    });
  }
};

export const videoTrackCreationFailedEffect: Effect<CreationFailed> = ({ payload: errorKey, meta }) => {
  // Specific tracking for backwards compatibility
  if (errorKey === 'deviceAlreadyInUse') {
    monitoring.trackAction('video_service.camera_already_in_use');
  }

  // General tracking for aggregated failure metrics
  monitoring.trackAction('video_service.camera_track_creation_failed', { errorKey, durationMs: meta.durationMs });
};

export const cameraDeviceChangeCompletedEffect: Effect<PayloadAction<string>> = ({ payload: deviceId }) => {
  monitoring.trackAction('video_service.camera_device_changed', { deviceId });
};

export const videoTrackTogglingFailedEffect: Effect = (_, { dispatch }) => {
  dispatch(addAppErrorMessage('Sorry we were unable to toggle your camera at this time. Please try again.'));
};

export const cameraDeviceChangeFailedEffect: Effect = (_, { dispatch, getState }) => {
  const currentCameraId = selectCurrentCameraDeviceId(getState());

  if (currentCameraId) {
    dispatch(setVideoDeviceId(currentCameraId));
  }
  dispatch(addAppErrorMessage('Sorry we were unable to change your camera at this time. Please try again.'));
};
